import { useId, useState } from "react";
import { cn } from "../lib/cn";

export type FaqItem = {
  question: string;
  answer: string;
};

function Icon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden="true"
      className={cn(
        "size-4 shrink-0 origin-center transition-transform duration-300",
        // „+“ um 45° gedreht ergibt das „×“ des geöffneten Zustands
        open && "rotate-45",
      )}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    >
      <line x1="3" y1="10" x2="17" y2="10" />
      <line x1="10" y1="3" x2="10" y2="17" />
    </svg>
  );
}

/**
 * FAQ-Liste. Wie im Design ist immer höchstens eine Frage geöffnet;
 * die erste ist beim Laden aufgeklappt.
 */
export function Accordion({
  items,
  className,
  defaultOpen = 0,
}: {
  items: FaqItem[];
  className?: string;
  /** Index der beim Laden geöffneten Frage, `null` für alle zu. */
  defaultOpen?: number | null;
}) {
  const [open, setOpen] = useState<number | null>(defaultOpen);
  const baseId = useId();

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {items.map((item, index) => {
        const isOpen = open === index;
        const panelId = `${baseId}-panel-${index}`;
        const buttonId = `${baseId}-button-${index}`;

        return (
          <div key={item.question}>
            <h3>
              <button
                id={buttonId}
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpen(isOpen ? null : index)}
                className={cn(
                  // Fragen stehen im Entwurf in Onest ExtraBold (20 pt),
                  // nicht in Open Sans – siehe „Element 632 ff.“.
                  "flex w-full cursor-pointer items-center justify-between gap-4 rounded-xl px-5 py-3 text-left font-onest text-sm font-extrabold transition-colors sm:px-6",
                  isOpen
                    ? "bg-linear-to-r from-[var(--brand-btn-from)] to-[var(--brand-btn-to)] text-[var(--brand-btn-text)]"
                    : "bg-[var(--brand-surface-strong)] text-white hover:bg-[var(--brand-surface-strong)]/80",
                )}
              >
                <span className="text-pretty">{item.question}</span>
                <Icon open={isOpen} />
              </button>
            </h3>

            <div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              hidden={!isOpen}
              className="px-5 pt-4 pb-2 text-[0.95rem] leading-relaxed text-pretty text-white/90 sm:px-6"
            >
              {item.answer}
            </div>
          </div>
        );
      })}
    </div>
  );
}
